import { Observable, combineLatest } from 'rxjs';
import { map, mergeMap } from 'rxjs/operators';

import { Injectable } from '@angular/core';

import {
  CurrencyUtils,
  PerformanceAccessoryAnswer,
  Study,
  StudyAhriCertificationType,
  StudyUnit,
  UnitPerformanceAccessoryAnswers,
} from '@mitsubishi/shared/data-access';
import { SpinnerService, StateSpinnerService } from '@mitsubishi/ui/spinner';
import { getStateActionErroredFactory } from '@mitsubishi/utils';

import { Dispatch } from '@ngxs-labs/dispatch-decorator';
import { Actions, Store } from '@ngxs/store';

import { Selection } from '../../models';
import { ProjectActions } from '../project/project.actions';
import { ProjectSelectors } from '../project/project.selectors';
import { StudyActions } from './study.actions';
import { StudySelectors } from './study.selectors';

@Injectable({
  providedIn: 'root',
})
export class StudyFacade extends StateSpinnerService {
  private onActionErrored = getStateActionErroredFactory();

  studies$: Observable<Study[]> = this.store.select(ProjectSelectors.projectStudies);
  activeStudy$ = this.store.select(StudySelectors.getActiveStudy);
  activeStudyGoodForSelectionUnits = this.store.selectSignal(StudySelectors.getGoodForSelectionUnits);
  isActiveStudyTemporary = this.store.selectSignal(StudySelectors.isActiveStudyTemporary);
  activeStudyId$ = this.store.select(StudySelectors.getRouteStudyId);
  studyUrl$ = combineLatest([this.store.select(ProjectSelectors.projectId), this.activeStudyId$]).pipe(
    map(([projectId, studyId]) => {
      return ['/', 'project', projectId, 'study', studyId];
    })
  );

  modelsAdditionProgress$ = this.store.select(StudySelectors.modelsAdditionProgress);
  unitId$ = this.store.select(StudySelectors.getRouteUnitId);
  units$ = this.activeStudy$.pipe(
    map(study =>
      Object.values(study?.subcategories ?? {})
        .map(elem => Object.values(elem.units))
        .reduce((res, units) => {
          return [...res, ...(units || [])];
        }, [])
    )
  );

  activeUnit$: Observable<StudyUnit | undefined> = this.store.select(StudySelectors.getActiveUnit);
  selectedUnit$: Observable<StudyUnit | undefined> = this.unitId$.pipe(
    mergeMap(unitId => this.units$.pipe(map(units => units?.find(unit => unit.uuid === unitId))))
  );

  currentStudyOrganizationId = this.store.selectSignal(StudySelectors.currentStudyOrganizationId);

  studyById$ = (studyId: string) => this.store.select(StudySelectors.getStudyById(studyId));
  externalUnitById$ = (unitId: string, workingConditionId: string) =>
    this.store.select(StudySelectors.getExternalUnit(unitId, workingConditionId));

  constructor(store: Store, spinnerService: SpinnerService, actions$: Actions) {
    super(store, spinnerService, actions$);

    this.addSpinnerActions([
      ProjectActions.Load,
      StudyActions.AddUnitToQuote,
      StudyActions.AssignPerformanceAccessories,
      StudyActions.AssignMultiplePerformanceAccessories,
      StudyActions.DuplicateUnitAndAssignPerformanceAccessories,
      StudyActions.DuplicateUnitAndAssignMultiplePerformanceAccessories,
      StudyActions.EditUnitConfiguration,
    ]);
    this.addSpinnerActions([StudyActions.ToggleGoodForSelection], true);
  }

  getCurrentProjectId(): string {
    return this.store.selectSnapshot(ProjectSelectors.projectId);
  }

  get activeStudyId() {
    return this.store.selectSnapshot(StudySelectors.getRouteStudyId);
  }

  get activeStudy() {
    return this.store.selectSnapshot(StudySelectors.getActiveStudy);
  }

  get isActiveStudyCertified() {
    return this.store.selectSnapshot(StudySelectors.isActiveStudyCertified);
  }

  get studySubcategories() {
    return this.store.selectSnapshot(StudySelectors.getActiveStudySubcategories);
  }

  get currencySymbol(): string {
    return CurrencyUtils.getCurrencySymbol(this.activeStudy?.currencyId);
  }

  getStudyById(studyId: string) {
    return this.store.selectSnapshot(StudySelectors.getStudyById(studyId));
  }

  getSubcategoryByUnitId(unitId: string) {
    return this.store.selectSnapshot(StudySelectors.getSubcategoryByUnitId(unitId));
  }

  getUnit(unitId: string | undefined) {
    return this.store.selectSnapshot(StudySelectors.getUnit(unitId));
  }

  getExternalUnit(unitId: string | undefined, workingConditionId: string) {
    return this.store.selectSnapshot(StudySelectors.getExternalUnit(unitId, workingConditionId));
  }

  // STUDY
  @Dispatch()
  open = (studyId: string) => new StudyActions.Open(studyId);

  @Dispatch()
  create = (certified: boolean, organizationId: number, systemType?: StudyAhriCertificationType) =>
    new StudyActions.Create(certified, organizationId, systemType);

  @Dispatch()
  delete = () => new StudyActions.Delete();

  @Dispatch()
  updateName = (name: string) => new StudyActions.UpdateName(name);

  @Dispatch()
  updateNote = (note: string) => new StudyActions.UpdateNote(note);

  updatePerformanceData = (
    subcategoryId: string,
    workingConditionId: string,
    performanceData: any,
    requestTraceId?: string
  ) => {
    this.store.dispatch(
      new StudyActions.UpdatePerformanceData(subcategoryId, workingConditionId, performanceData, requestTraceId)
    );

    return this.onActionErrored(StudyActions.UpdatePerformanceData);
  };

  createUnit = (modelSearchUnitId: string, requestTraceId?: string) => {
    this.store.dispatch(new StudyActions.CreateUnit(modelSearchUnitId, requestTraceId));

    return this.onActionErrored(StudyActions.CreateUnit);
  };

  @Dispatch()
  deleteUnit = (subcategoryId: string, unitId: string) => new StudyActions.DeleteUnit(subcategoryId, unitId);

  @Dispatch()
  toggleGoodForSelection = (unitId: string, subcategoryId: string, performanceId: string) =>
    new StudyActions.ToggleGoodForSelection(subcategoryId, unitId, performanceId);

  @Dispatch()
  updateWorkingCondition = (name: string, subcategoryId: string, workingConditionId: string) =>
    new StudyActions.UpdateWorkingCondition(name, subcategoryId, workingConditionId);

  createWorkingCondition = (subcategoryId: string, requestTraceId?: string) => {
    this.store.dispatch(new StudyActions.CreateWorkingCondition(subcategoryId, requestTraceId));

    return this.onActionErrored(StudyActions.CreateWorkingCondition);
  };

  @Dispatch()
  deleteWorkingCondition = (subcategoryId: string, workingConditionId: string) =>
    new StudyActions.DeleteWorkingCondition(subcategoryId, workingConditionId);

  @Dispatch()
  addUnitToQuote = (selection: Selection, unitUuid: string, performanceId: string) =>
    new StudyActions.AddUnitToQuote(selection, unitUuid, performanceId);

  @Dispatch()
  updateStudyUnitNote = (subcategoryId: string, unitUuid: string, workingConditionId: string, note: string) =>
    new StudyActions.UpdateStudyUnitNote(subcategoryId, unitUuid, workingConditionId, note);

  @Dispatch()
  updateAddBleenGroupProgress = (bleenGroupId: string, progress: number) =>
    new StudyActions.UpdateAddBleenGroupProgress(bleenGroupId, progress);

  @Dispatch()
  clearState = () => new StudyActions.ClearState();

  @Dispatch()
  deleteSubcategory = (subcategoryId: string) => new StudyActions.DeleteSubcategory(subcategoryId);

  @Dispatch()
  downloadPerformanceRequestLog = (workingConditionId: string, unitId: string) =>
    new StudyActions.DownloadPerformanceRequestLog(workingConditionId, unitId);

  @Dispatch()
  assignPerformanceAccessories = (
    performanceId: string,
    unitId: string,
    isTemporary: boolean,
    answers: PerformanceAccessoryAnswer[]
  ) => new StudyActions.AssignPerformanceAccessories(performanceId, unitId, isTemporary, answers);

  assignMultiplePerformanceAccessories = (
    unitAnswers: UnitPerformanceAccessoryAnswers[],
    isTemporary: boolean,
    requestTraceId?: string
  ) => {
    this.store.dispatch(
      new StudyActions.AssignMultiplePerformanceAccessories(isTemporary, unitAnswers, requestTraceId)
    );
    return this.onActionErrored(StudyActions.AssignMultiplePerformanceAccessories);
  };

  assignUnitAndAssignPerformanceAccessories = (
    performanceId: string,
    unitId: string,
    isTemporary: boolean,
    answers: PerformanceAccessoryAnswer[],
    requestTraceId?: string
  ) => {
    this.store.dispatch(
      new StudyActions.DuplicateUnitAndAssignPerformanceAccessories(
        performanceId,
        unitId,
        isTemporary,
        answers,
        requestTraceId
      )
    );
    return this.onActionErrored(StudyActions.DuplicateUnitAndAssignPerformanceAccessories);
  };

  assignUnitAndAssignMultiplePerformanceAccessories = (
    unitAnswers: UnitPerformanceAccessoryAnswers[],
    isTemporary: boolean,
    requestTraceId?: string
  ) => {
    this.store.dispatch(
      new StudyActions.DuplicateUnitAndAssignMultiplePerformanceAccessories(unitAnswers, isTemporary, requestTraceId)
    );
    return this.onActionErrored(StudyActions.DuplicateUnitAndAssignMultiplePerformanceAccessories);
  };

  @Dispatch()
  editUnitConfiguration = (
    studyId: string,
    unitId: string,
    performanceId: string,
    workingConditionId: string,
    fromStudy: boolean
  ) => new StudyActions.EditUnitConfiguration(studyId, unitId, performanceId, workingConditionId, fromStudy);

  addExternalUnit = (
    modelId: string,
    quantity: number,
    performanceId: string,
    workingConditions: any,
    requestTraceId?: string
  ) => {
    this.store.dispatch(
      new StudyActions.AddExternalUnit(modelId, quantity, performanceId, workingConditions, requestTraceId)
    );

    return this.onActionErrored(StudyActions.AddExternalUnit);
  };

  upgradeModels = (requestTraceId?: string) => {
    this.store.dispatch(new StudyActions.UpgradeModels(requestTraceId));

    return this.onActionErrored(StudyActions.UpgradeModels);
  };
}
